import { action } from "mobx";
import { roomEvents } from "@closerplatform/closer-sdk";
import { ID } from "@closerplatform/closer-sdk/dist/protocol/protocol";
import { markLastMessage } from "@fidget/common/closer";
import { IAttachment, IMessageItem } from "@fidget/common/models/chat/chatItems";
import { MessageTag } from "@fidget/common/models/messageTag";
import { ForeContext } from "../context";
import { MESSAGE_FILTERS } from "./common";
import { GuestProfile } from "@closerplatform/spinner-openapi";
import { apiActions } from "..";
import { IRoomState } from "../store/roomState";
import { staticConfig } from "@fidget/common/config/static-config";
import { Postback } from "../components/chat/richMessages/RichMessageTypes";

class UserActions {
  @action.bound
  closeButtonClicked(context: ForeContext) {
    context.hostWindowRequests.hideWidget();
  }

  @action.bound
  sendMessageWithPostback(context: ForeContext, text: string, postback?: Postback) {
    const { profile, room } = context.state;
    if (profile) {
      const pending = room.chat.createPendingMessage({ text });
      this.sendMessage(context, pending, profile, room, { postback });
    }
  }

  @action.bound
  sendButtonClicked(context: ForeContext, text: string, attachments?: ArrayLike<IAttachment>) {
    const { profile, room } = context.state;
    if (profile) {
      const pending = room.chat.createPendingMessage({ text, attachments });
      this.sendMessage(context, pending, profile, room);
    }
  }

  @action.bound
  sendFileDownloaded(context: ForeContext, fileUrl: string, fileId?: string, fileName?: string) {
    const { profile } = context.state;
    if (profile) {
      this.sendFileDownloadedMessage(context, profile, fileUrl, fileId, fileName);
    }
  }

  @action.bound
  sendLinkClicked(context: ForeContext, link: string, postback?: Postback) {
    const { profile } = context.state;
    if (profile) {
      this.sendLinkClickedMessage(context, profile, link, postback);
    }
  }

  @action.bound
  sendCobrowsingAcceptation(context: ForeContext, text: string) {
    const { profile, room } = context.state;
    if (profile) {
      const pending = room.chat.createPendingMessage({ text });
      this.sendCobrowsingAcceptationMessage(context, pending, profile, room);
    }
  }

  @action.bound
  sendCobrowsingEnded(context: ForeContext) {
    const profile = context.state.profile;
    if (profile) {
      this.sendCobrowsingEndedMessage(context, profile);
    }
  }

  @action.bound
  removeUndelivered(messageId: string, context?: ForeContext) {
    if (!context) {
      return undefined;
    }

    context.state.room.chat.removeUndelivered(messageId);

    return undefined;
  }

  @action.bound
  resendUndelievered(messageId: string, context?: ForeContext) {
    if (!context) {
      return undefined;
    }

    const { profile, room } = context.state;

    if (!profile) {
      return undefined;
    }

    const pending = context.state.room.chat.resendUndelivered(messageId);
    if (pending) {
      this.sendMessage(context, pending, profile, room);
    }

    return undefined;
  }

  @action.bound
  chatTopReached(context: ForeContext, roomId: ID) {
    const { room } = context.state;
    room.isLoading = true;
    context.artichokeService.pagedMessages(roomId, staticConfig.widgetPageSize, room.oldestTimestamp, {
      customFilter: MESSAGE_FILTERS,
      filter: [roomEvents.MessageSent.tag]
    }).then((page) => {
      apiActions.chatPageLoaded(
        context,
        page,
        page.length < staticConfig.widgetPageSize,
        // wtf is 10?
        // setting to `staticConfig.widgetPageSize` breaks scroll position
        // I guess that question is if there is only one page
        page.length < 10
      );
    });
  }

  @action.bound
  markAllMessages(context: ForeContext) {
    const { state } = context;
    if (state.chatIsVisible && state.profile && (state.tabIsActive || state.widgetIsActive) && state.chatScrolledToBottom) {
      markLastMessage(context, state.profile.roomId, state.room.chat.history);
      context.hostWindowRequests.setUnreadMessages(false);
    }
  }

  @action.bound
  chatIsScrolledToBottom(context: ForeContext, scrolledToBottom: boolean) {
    context.state.chatScrolledToBottom = scrolledToBottom;
    if (scrolledToBottom) {
      this.markAllMessages(context);
    }
  }

  @action.bound
  userTyping(context: ForeContext, preview?: string) {
    context.artichokeService.indicateTyping(context.state.profile!.roomId, preview);
  }

  @action.bound
  contactProvided(context: ForeContext, contact: string) {
    const { profile } = context.state;
    if (profile) {
      context.state.room.chat.removeContactEnquiryItem();
      return context.artichokeService.send(profile.roomId, contact, { agreement: true });
    }
    return Promise.reject();
  }

  @action.bound
  scheduleMeeting(context: ForeContext, token: string, start: number, duration: number) {
    context.spinnerService.scheduleMeeting(token, start, duration).then((meeting) => {
      context.state.pendingMeetingToken = undefined;
    });
  }

  @action.bound
  cancelMeetingScheduling(context: ForeContext, token: string) {
    context.spinnerService.cancelMeetingScheduling(token).finally(() => {
      // on widget, only one scheduling "process" can exist, so we can
      // safely unset the meeting token to end the scheduling flow
      context.state.pendingMeetingToken = undefined;
    });
  }

  private sendMessage(
    context: ForeContext,
    pending: IMessageItem,
    profile: GuestProfile,
    room: IRoomState,
    messageContext?: any,
  ) {
    const { text, attachments } = pending;
    const sendPromise =
      attachments && attachments.length
        ? context.artichokeService.sendCustom(
          profile.roomId,
          "",
          MessageTag.MESSAGE_WITH_ATTACHMENTS,
          { message: text, attachments }
        )
        : context.artichokeService.send(profile.roomId, text, messageContext);

    sendPromise
      .then((received) => room.chat.pendingMessageSent(pending.id, received))
      .catch((e) => {
        room.chat.pendingMessageFailed(pending.id);
      });
    context.hostWindowRequests.messageSent();
  }
  private sendFileDownloadedMessage(
    context: ForeContext,
    profile: GuestProfile,
    fileUrl: string,
    fileId?: string,
    fileName?: string,
  ) {
    context.artichokeService
      .sendCustom(profile.roomId, "", MessageTag.FILE_DOWNLOADED, { fileUrl, fileId, fileName });
  }

  private sendLinkClickedMessage(
    context: ForeContext,
    profile: GuestProfile,
    link: string,
    postback?: Postback
  ) {
    const messageContext = { link, postback };
    context.artichokeService
      .sendCustom(profile.roomId, "", MessageTag.LINK_CLICKED, messageContext);
  }

  private sendCobrowsingAcceptationMessage(
    context: ForeContext,
    pending: IMessageItem,
    profile: GuestProfile,
    room: IRoomState
  ) {
    const { text } = pending;
    const sessionId = profile.roomId;
    context.hostWindowRequests.cobrowsingSessionInitialized(sessionId);
    context.artichokeService
      .sendCustom(profile.roomId, text, MessageTag.COBROWSING_ACCEPTATION, { sessionId })
      .then((received) => room.chat.pendingMessageSent(pending.id, received))
      .catch((e) => {
        room.chat.pendingMessageFailed(pending.id);
      });
    context.hostWindowRequests.messageSent();
  }

  private sendCobrowsingEndedMessage(context: ForeContext, profile: GuestProfile) {
    context.artichokeService.sendCustom(profile.roomId, "", MessageTag.COBROWSING_ENDED, {});
    context.hostWindowRequests.messageSent();
  }
}

export const userActions = new UserActions();
